import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  MapPinIcon,
  StarIcon,
  ClockIcon,
  PhoneIcon,
  ArrowLeftIcon,
  HeartIcon,
  ShareIcon,
  ChevronRightIcon,
  CalendarIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import { HeartIcon as HeartSolidIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid'
import { supabase } from '../config/supabase'
import toast from 'react-hot-toast'
import CustomerLoader from '../components/customer/CustomerLoader'

const ACTION_GREEN = '#00C853'
const BRAND_GREEN = '#00E676'

const RestaurantDetails = () => {
  const { restaurantId } = useParams()
  const navigate = useNavigate()
  const [restaurant, setRestaurant] = useState(null)
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [isFavorite, setIsFavorite] = useState(false)
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  useEffect(() => {
    if (restaurantId) {
      fetchRestaurantDetails()
    }
  }, [restaurantId])

  const fetchRestaurantDetails = async () => {
    try {
      setLoading(true)
      
      // Fetch restaurant details
      const { data: restaurantData, error: restaurantError } = await supabase
        .from('users')
        .select(`
          id,
          restaurant_name,
          restaurant_description,
          restaurant_address,
          restaurant_phone,
          restaurant_email,
          cuisine_type,
          logo_url,
          banner_url,
          opening_hours,
          is_active,
          is_open
        `)
        .eq('id', restaurantId)
        .eq('role', 'restaurant_owner')
        .single()

      if (restaurantError) throw restaurantError

      // Fetch reviews
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select(`
          id,
          overall_rating,
          food_rating,
          service_rating,
          ambiance_rating,
          comment,
          created_at,
          customer_name
        `)
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false })
        .limit(10)

      if (reviewsError) throw reviewsError

      const avgRating = reviewsData?.length > 0 
        ? reviewsData.reduce((sum, review) => sum + review.overall_rating, 0) / reviewsData.length
        : 4.2

      setRestaurant({
        ...restaurantData,
        name: restaurantData.restaurant_name,
        description: restaurantData.restaurant_description,
        address: restaurantData.restaurant_address,
        phone: restaurantData.restaurant_phone,
        rating: Number(avgRating.toFixed(1)),
        review_count: reviewsData?.length || 0
      })
      setReviews(reviewsData || [])
    } catch (error) {
      console.error('Error fetching restaurant details:', error)
      toast.error('Failed to load restaurant details')
    } finally {
      setLoading(false)
    }
  }

  const toggleFavorite = () => {
    setIsFavorite(!isFavorite)
    toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites! ❤️')
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: restaurant?.name,
        text: `Check out ${restaurant?.name} on Ordyrr!`,
        url: window.location.href
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      toast.success('Link copied to clipboard!')
    }
  }

  const isCurrentlyOpen = () => {
    if (!restaurant?.opening_hours) return true
    
    try {
      const hours = restaurant.opening_hours
      const now = new Date()
      const istOffset = 5.5 * 60 * 60 * 1000
      const istTime = new Date(now.getTime() + istOffset)
      const today = istTime.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase()
      const todayHours = hours[today]
      
      if (!todayHours || todayHours.closed === true) return false
      
      const currentHour = istTime.getUTCHours().toString().padStart(2, '0')
      const currentMinute = istTime.getUTCMinutes().toString().padStart(2, '0')
      const currentTime = `${currentHour}:${currentMinute}`
      
      const timeToMinutes = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number)
        return hours * 60 + minutes
      }
      
      const currentMinutes = timeToMinutes(currentTime)
      const openMinutes = timeToMinutes(todayHours.open)
      const closeMinutes = timeToMinutes(todayHours.close)
      
      return currentMinutes >= openMinutes && currentMinutes <= closeMinutes
    } catch (error) {
      return true
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <CustomerLoader />
      </div>
    )
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="text-6xl mb-4">🍽️</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Restaurant not found</h3>
          <button
            onClick={() => navigate(-1)}
            className="text-green-600 font-semibold"
          >
            Go back
          </button>
        </div>
      </div>
    )
  }

  const restaurantImages = [
    restaurant.banner_url || restaurant.logo_url,
    // Add more images if available
  ].filter(Boolean)

  return (
    <div className="min-h-screen bg-white pb-24">
      {/* Image Gallery with Navigation */}
      <div className="relative h-80 bg-gradient-to-br from-gray-100 to-gray-200">
        {restaurantImages.length > 0 ? (
          <img
            src={restaurantImages[currentImageIndex]}
            alt={restaurant.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-8xl">🍽️</span>
          </div>
        )}

        {/* Top Action Buttons */}
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg"
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-700" />
          </motion.button>

          <div className="flex items-center gap-2">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={toggleFavorite}
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg"
            >
              {isFavorite ? (
                <HeartSolidIcon className="w-5 h-5 text-red-500" />
              ) : (
                <HeartIcon className="w-5 h-5 text-gray-700" />
              )}
            </motion.button>

            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={handleShare}
              className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg"
            >
              <ShareIcon className="w-5 h-5 text-gray-700" />
            </motion.button>
          </div>
        </div>

        {/* View Gallery Button */}
        {restaurantImages.length > 1 && (
          <div className="absolute bottom-4 right-4">
            <button className="px-4 py-2 bg-white rounded-lg text-sm font-semibold shadow-lg">
              View Gallery ›
            </button>
          </div>
        )}
      </div>

      {/* Restaurant Info Card */}
      <div className="px-4 -mt-6 relative z-10">
        <div className="bg-white rounded-2xl p-4 shadow-lg">
          {/* Restaurant Name & Rating */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-1">{restaurant.name}</h1>
              <p className="text-sm text-gray-600">
                {restaurant.address || '3.8 km • Pacific Mall, NSP, Delhi'}
              </p>
            </div>
            
            <div 
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg flex-shrink-0"
              style={{ backgroundColor: ACTION_GREEN }}
            >
              <StarSolidIcon className="w-4 h-4 text-white" />
              <span className="text-white font-bold text-sm">{restaurant.rating}</span>
              <span className="text-white text-xs">★</span>
            </div>
          </div>

          {/* Cuisine & Cost */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <span className="font-medium capitalize">{restaurant.cuisine_type || 'Multi-cuisine'}</span>
            <span>|</span>
            <span>₹1500 for two</span>
          </div>

          {/* Status & Timing */}
          <div className="flex items-center gap-2 mb-3">
            {isCurrentlyOpen() ? (
              <span className="text-sm font-semibold text-green-600">
                Open • Opens at 12PM
              </span>
            ) : (
              <span className="text-sm font-semibold text-red-600">
                Closed • Opens at 12PM
              </span>
            )}
            <button className="ml-auto">
              <ChevronRightIcon className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Rating Count */}
          <p className="text-xs text-gray-500">{restaurant.review_count} ratings</p>
        </div>
      </div>

      {/* Event/Offer Banner */}
      <div className="px-4 mt-4">
        <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-4 border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-bold text-green-700 bg-green-200 px-2 py-1 rounded">
              🎉 EVENT
            </span>
          </div>
          <p className="text-sm font-semibold text-gray-900 mb-1">
            Use up to ₹200 DineCash • Earn 10% more
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-4 mt-4 flex gap-3">
        <motion.button
          whileTap={{ scale: 0.98 }}
          onClick={() => setShowBookingModal(true)}
          className="flex-1 py-4 rounded-xl font-bold text-base border-2 border-green-600 text-green-700 bg-white"
        >
          Book a table
        </motion.button>

        <motion.button
          whileTap={{ scale: 0.98 }}
          className="flex-1 py-4 rounded-xl font-bold text-base text-white"
          style={{ backgroundColor: ACTION_GREEN }}
        >
          Pay bill now
        </motion.button>
      </div>

      {/* Description */}
      {restaurant.description && (
        <div className="px-4 mt-6">
          <h2 className="text-lg font-bold text-gray-900 mb-2">About</h2>
          <p className="text-sm text-gray-600 leading-relaxed">
            {restaurant.description}
          </p>
        </div>
      )}

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <div className="px-4 mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Reviews</h2>
            <button className="text-sm font-semibold text-green-600">
              See all ›
            </button>
          </div>

          <div className="space-y-4">
            {reviews.slice(0, 3).map((review) => (
              <div key={review.id} className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div 
                    className="flex items-center gap-1 px-2 py-1 rounded-lg"
                    style={{ backgroundColor: ACTION_GREEN }}
                  >
                    <StarSolidIcon className="w-3 h-3 text-white" />
                    <span className="text-white font-bold text-xs">{review.overall_rating}</span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(review.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-gray-700">{review.comment}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Booking Modal */}
      <AnimatePresence>
        {showBookingModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-end"
            onClick={() => setShowBookingModal(false)}
          >
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white w-full rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6"></div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Book a table</h2>
              
              <div className="text-center py-12">
                <CalendarIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Booking flow coming soon!</p>
                <button
                  onClick={() => navigate(`/booking/${restaurantId}`)}
                  className="px-6 py-3 rounded-xl font-bold text-white"
                  style={{ backgroundColor: ACTION_GREEN }}
                >
                  Continue to Booking
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default RestaurantDetails
